/**
 * Viewer-personal preferences.
 *
 * These are per-browser settings that override owner defaults. Stored in
 * localStorage so they survive reloads without requiring login. When the
 * viewer signs in (Phase 3), the same prefs object is hydrated from the
 * server and any local changes are flushed there.
 *
 * Schema:
 *   {
 *     volume: number 0..10,                          // remember last volume
 *     starting_channel_id: string | null,            // channel to land on (null = default)
 *     phosphor_override: "green"|"amber"|"blue"|"white"|null,  // null = owner theme
 *     reduce_motion: boolean,                        // kill static blip + slide
 *     silent_auto_skip: boolean,                     // no "embed refused" overlay
 *   }
 */
const KEY = "crt_viewer_prefs_v1";

export const DEFAULT_PREFS = Object.freeze({
  // CRT widescreen presentation inside the native 4:3 tube.
  // Remote PICTURE cycles the same five modes.
  picture_mode: "letterbox",
  volume: 6,
  starting_channel_id: null,
  phosphor_override: null,
  skin_override: null,
  room_override: null,
  reduce_motion: false,
  silent_auto_skip: false,
  // CRT power-on warmup animation — quick phosphor flash + collapsing
  // scan-line sweep when the TV is turned on. Pure retro theatre; viewers
  // who hate it can toggle it off in PREFS. Honoured only when not also
  // suppressed by `reduce_motion`.
  power_on_animation: true,
  // CRT tube glow — permanent part of the scartlead.tv CRT presentation.
  // Kept in the preference schema for backwards compatibility, but App.js
  // now treats the glow itself as always enabled.
  tube_glow: true,
  // Glow intensity 0-100. Video-reactive Ambilight defaults to full intended strength.
  // and inner phosphor edge. 0 ≈ off, 100 ≈ heavy "tube under load".
  tube_glow_intensity: 100,
  // Optional hex colour override (e.g. "#ffaa33" for amber, "#66ccff"
  // for cool blue). When null the glow tracks the active phosphor theme.
  tube_glow_color: null,
  // Breath animation — optional slow ambient tube pulse.
  // Disabled by default; viewers can opt in from MY PREFS.
  tube_glow_animated: false,
  // Audio-reactive glow — when on, the glow swells with loud audio
  // and dims during quiet moments using the Web Audio API analyser
  // wired into the playing <video> element. Overrides the static
  // breath animation when both are enabled. YouTube embeds can't be
  // analysed cross-origin so the effect just no-ops there. Off by
  // default — opt-in for the full responsive-CRT theatre.
  tube_glow_audio_reactive: false,
  // Where the cabinet lands on a brand-new visit. Four modes:
  //   "first"    = first non-virtual channel (default — the legacy behaviour)
  //   "last"     = pick up on the last channel I was watching (uses the
  //                `crt_last_channel_id` localStorage value, written on
  //                every tune change)
  //   "fixed"    = always start on the channel saved in `starting_channel_id`
  //                (legacy `starting_channel_id != null` is silently treated
  //                as this mode for backwards compatibility on first run)
  //   "surprise" = random non-virtual channel each visit
  starting_channel_mode: "first",
  // ★ Favourites — list of video ids the viewer has starred. Stored here
  // (instead of the legacy `crt_favourites` key) so a signed-in viewer's
  // stars sync to the server alongside the rest of their prefs.
  favourites: [],
  // ★ Favourite channels — separate from `favourites` above (which is
  // per-video). The streaming-dashboard's HOME tab shows starred
  // channels as a row of thumbnails; the flag lives here so signed-in
  // viewers get server-sync alongside the rest of their prefs.
  favourite_channels: [],
  // Default rail the Channel Guide opens on. "__my_tv__" = the MY TV
  // virtual rail (★ FAVOURITES + ✉ CONTACT); a category id pins on that
  // specific rail; null = behave as if "__my_tv__" was set. Owner sets
  // this from the guide's per-rail "★ DEFAULT" toggle so the very next
  // guide-open lands here.
  default_guide_rail: "__my_tv__",
  // Channel Guide tile density — max tiles per row in each rail. `null`
  // = auto-fit (measure the container and pack as many cards as fit,
  // up to the built-in ceiling). Any integer in [3, 20] hard-caps the
  // page size instead, so viewers on ultrawide monitors can force a
  // tighter grid ("show me exactly 12 per row"). Only affects the
  // Channel Guide rails, not the Channel Browse grid.
  guide_tiles_per_row: null,
  // Cabinet scale — grows/shrinks the CRT + remote combo. `null` =
  // AUTO, which detects viewport width and scales the pair up on
  // large monitors (baseline ~1440px). A number in [0.75, 1.75]
  // pins the scale manually. Applied via a `--cabinet-scale` CSS
  // variable that CRT/Remote multiply their max-widths against.
  cabinet_scale: null,
  // ★ Patron perk — hide on-screen ident distractions (top-left channel
  // logo watermark, bottom-left SCARTLEAD.TV bezel wordmark). Only
  // surfaced in PREFS once the viewer has unlocked the PATRON trophy;
  // auto-enabled on first unlock via a one-shot localStorage flag
  // (`crt_patron_auto_hide_applied`) so returning patrons don't get
  // their manual "no thanks, keep them on" preference overwritten.
  hide_distractions: false,
});

export function getPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed };
  } catch (e) {
    return { ...DEFAULT_PREFS };
  }
}

export function setPrefs(next) {
  // Replace-not-merge so callers always commit the full doc; missing fields
  // fall back to DEFAULT_PREFS via getPrefs(). Validation: every value must
  // be assignable to a key in DEFAULT_PREFS.
  try {
    const sanitised = {};
    for (const k of Object.keys(DEFAULT_PREFS)) {
      sanitised[k] = (k in next) ? next[k] : DEFAULT_PREFS[k];
    }
    localStorage.setItem(KEY, JSON.stringify(sanitised));
    // Broadcast so any open mounts can re-hydrate without a reload.
    window.dispatchEvent(new CustomEvent("crt:prefs-updated", { detail: sanitised }));
    return sanitised;
  } catch (e) {
    return next;
  }
}

export function patchPrefs(patch) {
  return setPrefs({ ...getPrefs(), ...patch });
}

export function resetPrefs() {
  try { localStorage.removeItem(KEY); } catch (e) { /* noop */ }
  window.dispatchEvent(new CustomEvent("crt:prefs-updated", { detail: { ...DEFAULT_PREFS } }));
  return { ...DEFAULT_PREFS };
}
